import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';

/**
 * Persists access/refresh tokens and user snapshot.
 *
 * - Refresh token is always in `localStorage` so new browser tabs can bootstrap via `/auth/refresh`.
 * - Access token + user snapshot: `localStorage` when "Remember me", else `sessionStorage` (tab-scoped).
 * - Session medium marker lives in `localStorage` so new tabs know where to read access tokens.
 */
@Injectable({ providedIn: 'root' })
export class TokenStorageService {
  private readonly document = inject(DOCUMENT);

  static readonly ACCESS = 'aps_auth_access';
  static readonly REFRESH = 'aps_auth_refresh';
  static readonly USER = 'aps_auth_user';
  static readonly ACCESS_EXP = 'aps_auth_access_exp';
  static readonly MEDIUM = 'aps_auth_medium';
  /** Last workspace client per user (localStorage; restored after login only). */
  static readonly PREFERRED_CLIENT = 'aps_workspace_client';
  /** Last workspace branch per user + client (localStorage). */
  static readonly PREFERRED_BRANCH = 'aps_workspace_branch';

  /**
   * Rotates tokens in place (refresh flow). Does not clear storage first so other tabs
   * never see a transient missing refresh token.
   */
  updateSessionTokens(data: {
    accessToken: string;
    refreshToken: string;
    accessExpiresAtMs: number;
    userJson: string;
    rememberMe: boolean;
  }): void {
    const win = this.document.defaultView;
    if (!win) {
      return;
    }

    const medium = data.rememberMe ? 'local' : 'session';
    const accessStorage = data.rememberMe ? win.localStorage : win.sessionStorage;

    win.localStorage.setItem(TokenStorageService.MEDIUM, medium);
    win.localStorage.setItem(TokenStorageService.REFRESH, data.refreshToken);

    accessStorage.setItem(TokenStorageService.ACCESS, data.accessToken);
    accessStorage.setItem(TokenStorageService.ACCESS_EXP, String(data.accessExpiresAtMs));
    accessStorage.setItem(TokenStorageService.USER, data.userJson);
    if (!data.rememberMe) {
      accessStorage.setItem(TokenStorageService.MEDIUM, medium);
    }
  }

  setSessionData(data: {
    accessToken: string;
    refreshToken: string;
    accessExpiresAtMs: number;
    userJson: string;
    rememberMe: boolean;
  }): void {
    this.clearBoth();
    const win = this.document.defaultView;
    if (!win) {
      return;
    }

    const medium = data.rememberMe ? 'local' : 'session';
    const accessStorage = data.rememberMe ? win.localStorage : win.sessionStorage;

    win.localStorage.setItem(TokenStorageService.MEDIUM, medium);
    win.localStorage.setItem(TokenStorageService.REFRESH, data.refreshToken);

    accessStorage.setItem(TokenStorageService.ACCESS, data.accessToken);
    accessStorage.setItem(TokenStorageService.ACCESS_EXP, String(data.accessExpiresAtMs));
    accessStorage.setItem(TokenStorageService.USER, data.userJson);
    if (!data.rememberMe) {
      accessStorage.setItem(TokenStorageService.MEDIUM, medium);
    }
  }

  /**
   * After switch-client: persists the API access JWT verbatim (no trim/parse/re-sign).
   * Refresh token is unchanged.
   */
  updateAccessOnly(accessToken: string, accessExpiresAtMs: number): void {
    const storage = this.getAccessStorage();
    if (!storage || !accessToken.length) {
      return;
    }
    storage.setItem(TokenStorageService.ACCESS, accessToken);
    storage.setItem(TokenStorageService.ACCESS_EXP, String(accessExpiresAtMs));
  }

  getActiveMedium(): 'session' | 'local' | null {
    const win = this.document.defaultView;
    if (!win) {
      return null;
    }

    const marker = win.localStorage.getItem(TokenStorageService.MEDIUM);
    if (marker === 'local' || marker === 'session') {
      return marker;
    }

    /* Legacy fallbacks before medium marker was stored in localStorage. */
    if (win.localStorage.getItem(TokenStorageService.ACCESS)) {
      return 'local';
    }
    if (win.sessionStorage.getItem(TokenStorageService.ACCESS)) {
      return 'session';
    }
    if (win.localStorage.getItem(TokenStorageService.REFRESH)) {
      return 'session';
    }
    return null;
  }

  /** Storage for access token, expiry, and user snapshot. */
  getAccessStorage(): Storage | null {
    const win = this.document.defaultView;
    if (!win) {
      return null;
    }
    const m = this.getActiveMedium();
    if (m === 'local') {
      return win.localStorage;
    }
    if (m === 'session') {
      return win.sessionStorage;
    }
    if (win.sessionStorage.getItem(TokenStorageService.ACCESS)) {
      return win.sessionStorage;
    }
    if (win.localStorage.getItem(TokenStorageService.ACCESS)) {
      return win.localStorage;
    }
    return win.sessionStorage;
  }

  getAccessToken(): string | null {
    return this.getAccessStorage()?.getItem(TokenStorageService.ACCESS) ?? null;
  }

  getRefreshToken(): string | null {
    const win = this.document.defaultView;
    if (!win) {
      return null;
    }
    return (
      win.localStorage.getItem(TokenStorageService.REFRESH) ??
      /* Legacy: refresh lived only in sessionStorage. */
      win.sessionStorage.getItem(TokenStorageService.REFRESH)
    );
  }

  getAccessExpiresAtMs(): number | null {
    const raw = this.getAccessStorage()?.getItem(TokenStorageService.ACCESS_EXP);
    if (!raw) {
      return null;
    }
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }

  getUserJson(): string | null {
    return this.getAccessStorage()?.getItem(TokenStorageService.USER) ?? null;
  }

  setPreferredWorkspaceClient(userId: string, clientId: string): void {
    const win = this.document.defaultView;
    if (!win || !userId.trim() || !clientId.trim()) {
      return;
    }
    try {
      win.localStorage.setItem(
        TokenStorageService.PREFERRED_CLIENT,
        JSON.stringify({ userId: userId.trim(), clientId: clientId.trim() }),
      );
    } catch {
      /* ignore quota / private mode */
    }
  }

  getPreferredWorkspaceClient(userId: string): string | null {
    const win = this.document.defaultView;
    if (!win || !userId.trim()) {
      return null;
    }
    try {
      const raw = win.localStorage.getItem(TokenStorageService.PREFERRED_CLIENT);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as { userId?: string; clientId?: string };
      if (parsed?.userId !== userId.trim() || typeof parsed.clientId !== 'string') {
        return null;
      }
      const id = parsed.clientId.trim();
      return id.length ? id : null;
    } catch {
      return null;
    }
  }

  clearPreferredWorkspaceClient(): void {
    const win = this.document.defaultView;
    if (!win) {
      return;
    }
    try {
      win.localStorage.removeItem(TokenStorageService.PREFERRED_CLIENT);
    } catch {
      /* ignore */
    }
  }

  setPreferredWorkspaceBranch(userId: string, clientId: string, branchId: string): void {
    const win = this.document.defaultView;
    if (!win || !userId.trim() || !clientId.trim() || !branchId.trim()) {
      return;
    }
    try {
      win.localStorage.setItem(
        TokenStorageService.PREFERRED_BRANCH,
        JSON.stringify({
          userId: userId.trim(),
          clientId: clientId.trim(),
          branchId: branchId.trim(),
        }),
      );
    } catch {
      /* ignore */
    }
  }

  getPreferredWorkspaceBranch(userId: string, clientId: string): string | null {
    const win = this.document.defaultView;
    if (!win || !userId.trim() || !clientId.trim()) {
      return null;
    }
    try {
      const raw = win.localStorage.getItem(TokenStorageService.PREFERRED_BRANCH);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as {
        userId?: string;
        clientId?: string;
        branchId?: string;
      };
      if (
        parsed?.userId !== userId.trim() ||
        parsed?.clientId !== clientId.trim() ||
        typeof parsed.branchId !== 'string'
      ) {
        return null;
      }
      const id = parsed.branchId.trim();
      return id.length ? id : null;
    } catch {
      return null;
    }
  }

  clearBoth(): void {
    const win = this.document.defaultView;
    if (!win) {
      return;
    }
    this.clearOne(win.sessionStorage);
    this.clearOne(win.localStorage);
    win.localStorage.removeItem(TokenStorageService.REFRESH);
    win.localStorage.removeItem(TokenStorageService.MEDIUM);
    /* Keep aps_workspace_client — survives logout within the same browser profile if user logs back in. */
  }

  private clearOne(s: Storage): void {
    s.removeItem(TokenStorageService.ACCESS);
    s.removeItem(TokenStorageService.REFRESH);
    s.removeItem(TokenStorageService.USER);
    s.removeItem(TokenStorageService.ACCESS_EXP);
    s.removeItem(TokenStorageService.MEDIUM);
  }
}
